import { useState, useEffect } from 'react';
import { Box, Typography, Stack, Paper, Avatar } from '@mui/material';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import ShareYourIdeasDialog from './ShareYourIdeasDialog';
import Logo from './../../icons/logo.svg';
import DefaultCompany from './../../icons/building_round.svg';
import { getCompanyActivity } from '../../services/company_services';
import EmptyState from './EmptyState';
import LoadingLogoSkeleton from '../SkeletonLoading/loadingLogoSkeleton';

export default function BoardroomActivity({ company, showEmptyState }) {
  const [shareYourIdeasDialogOpen, setShareYourIdeasDialogOpen] =
    useState(false);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const { accountDetails } = useAccount();
  const getAvatarUrl = (activity) => {
    if (activity.activityType === 'surveyResponse') {
      return company?.image?.id
        ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
        : DefaultCompany;
    }
    return `${process.env.REACT_APP_IMAGES_URL}${activity.advisor?.image?.imageKey}`;
  };

  useEffect(() => {
    let isMounted = true;
    if (company.id) {
      setLoading(true);
      getCompanyActivity({
        companyId: company.id,
        filterType: ['advisorJoinedToCompany', 'surveyResponse', 'advisorIdea'],
      })
        .then((response) => {
          if (isMounted) {
            setActivities(response.data.getCompanyActivity || []);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error('Error fetching company activity:', error);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [company]);

  useEffect(() => {
    eventBus.on(eventBusValues.surveyAnswered, (activity) => {
      setActivities((prevActivities) => [activity, ...prevActivities]);
    });
    eventBus.on(eventBusValues.introAdded, (text) => {
      setActivities((prevActivities) => {
        const currentUserActivity = prevActivities.find(
          (item) =>
            item.activityType === 'advisorJoinedToCompany' &&
            item.advisorId === accountDetails.id
        );
        if (currentUserActivity) {
          return prevActivities.map((item) =>
            item.id === currentUserActivity.id ? { ...item, label: text } : item
          );
        }
        return prevActivities;
      });
    });

    return () => {
      eventBus.remove(eventBusValues.surveyAnswered);
      eventBus.remove(eventBusValues.introAdded);
    };
  }, []);

  const renderLabel = (label, advisorId) => {
    const segments = label.split(/(<b>.*?<\/b>)/g);
    let firstBold = true;

    return (
      <Typography sx={{ fontWeight: '400', color: '#4D586F' }}>
        {segments.map((segment, idx) => {
          const match = segment.match(/<b>(.*?)<\/b>/);
          if (match) {
            const text = match[1];
            if (firstBold) {
              firstBold = false;
              return (
                <Box
                  key={idx}
                  component="span"
                  sx={{
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#001030',
                  }}
                  onClick={() => openProfile(advisorId)}
                >
                  {text}
                </Box>
              );
            } else {
              return (
                <Box key={idx} component="span" sx={{ fontWeight: '600' }}>
                  {text}
                </Box>
              );
            }
          }
          return (
            <Box key={idx} component="span">
              {segment}
            </Box>
          );
        })}
      </Typography>
    );
  };

  function openProfile(advisorId) {
    if (!advisorId) {
      console.error('Advisor ID is not provided');
      return;
    }
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      fetchAdvisor: true,
      id: advisorId,
      boardRoomView: true,
    });
  }

  if (loading) {
    return (
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingLogoSkeleton width='200px' height='200px' />
      </Paper>
    );
  }

  if (activities.length === 0 && showEmptyState) {
    return <EmptyState />;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          padding: '32px',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mb: 2,
          }}
        >
          <Avatar
            alt={`${company.companyName} Logo`}
            src={Logo}
            sx={{
              width: 32,
              height: 32,
              mt: '-4px',
            }}
          />
          <Typography
            sx={{
              mb: 2,
              fontWeight: '600',
              fontSize: '20px',
              lineHeight: '130%',
              color: '#001030',
              ml: '20px',
            }}
          >
            Boardroom Activity
          </Typography>
        </Box>
        <Stack spacing={2}>
          {activities.length > 0 ? (
            activities.map((activity, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Avatar
                  alt={activity.advisor?.displayName}
                  src={getAvatarUrl(activity)}
                  sx={{
                    width: 40,
                    height: 40,
                    marginRight: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    openProfile(activity.advisor?.id || activity.advisorId)
                  }
                />
                <Box sx={{ flexGrow: 1 }}>
                  {renderLabel(activity.label, activity.advisorId)}
                </Box>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No activity found.
            </Typography>
          )}
        </Stack>
      </Paper>
      <ShareYourIdeasDialog
        open={shareYourIdeasDialogOpen}
        onClose={() => setShareYourIdeasDialogOpen(false)}
        company={company}
      />
    </Box>
  );
}
